/**
 * 로그인/지점선택 후 되돌아갈 경로 결정.
 *
 * 🔴 왜 필요한가 (2026-08-06)
 *   `ProtectedRoute` 는 미인증이면 `/login` 으로 보내는데, 예전에는 **요청 경로를 버렸고**
 *   로그인 후에도 항상 `/` 로 갔다. 그래서 폰으로 `…/zoolungman` 링크를 열면 로그인 뒤
 *   메뉴로 떨어져 원하는 화면을 다시 찾아 들어가야 했다.
 *   이제 `ProtectedRoute` 가 `state.from` 에 실어 보내고, 이 함수가 그것을 해석한다.
 *
 * 🔴 왜 값 검증이 필요한가
 *   `state` 는 히스토리에 실려 오는 값이라 그대로 믿고 `navigate()` 에 넘기면 안 된다.
 *   특히 `//evil.com` 은 **프로토콜 상대 URL** 이라 브라우저가 외부로 해석한다.
 *   그래서 "`/` 로 시작하고 `//` 로 시작하지 않는" 내부 경로만 통과시킨다.
 *
 * ⚠️ 되돌아가면 안 되는 경로가 있다:
 *   · `/login`, `/store-select` → 로그인 직후 다시 그 화면으로 보내면 제자리걸음이다.
 *
 * 계약 고정: `tools/src/utils/redirectAfterLogin.test.js`
 */

/** 로그인 흐름 자체의 화면 — 복원 대상에서 제외한다. */
export const AUTH_FLOW_PATHS = Object.freeze(['/login', '/store-select']);

/** 복원할 곳이 없거나 부적절할 때의 기본 목적지. */
export const DEFAULT_AFTER_LOGIN = '/';

/**
 * @param {object} [locationState] `useLocation().state` (없을 수 있음)
 * @returns {string} 이동할 경로 (항상 내부 경로)
 */
export default function redirectAfterLogin(locationState) {
  const from = locationState && locationState.from;
  if (!from) return DEFAULT_AFTER_LOGIN;

  // Location 객체(pathname/search/hash) 또는 문자열 둘 다 받는다.
  const pathname = typeof from === 'string' ? from : from.pathname;
  if (typeof pathname !== 'string' || !pathname) return DEFAULT_AFTER_LOGIN;

  // 내부 절대경로만 허용 — `//evil.com`(프로토콜 상대), `http://…` 는 거부.
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return DEFAULT_AFTER_LOGIN;

  if (AUTH_FLOW_PATHS.includes(pathname)) return DEFAULT_AFTER_LOGIN;

  // 쿼리·해시는 보존한다 — 링크에 담긴 파라미터가 사라지면 딥링크의 의미가 준다.
  const search = typeof from === 'string' ? '' : (from.search || '');
  const hash = typeof from === 'string' ? '' : (from.hash || '');
  return `${pathname}${search}${hash}`;
}
